import { useEffect, useState } from "react";
import { Logo } from "@/components/Logo";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck, Loader2, Check, CalendarClock, MapPin, Clock, CalendarPlus } from "lucide-react";
import { api, apiErr } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicAppt() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    api.get(`/public/appt/${token}`)
      .then(({ data }) => { setInfo(data); setConfirmed(!!data.customer_confirmed); })
      .catch(() => setNotFound(true));
  }, [token]);

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/public/appt/${token}/confirm`);
      setConfirmed(true);
      setInfo((s) => ({ ...s, status: "confirmed" }));
      toast.success("Appointment confirmed");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (notFound) return <div className="grid min-h-screen place-items-center bg-[#0B0F17] text-slate-400" data-testid="appt-notfound">This appointment link is not available.</div>;

  return (
    <div className="min-h-screen bg-[#0B0F17] px-4 py-12 text-slate-200" data-testid="public-appt-page">
      <div className="mx-auto max-w-md">
        <div className="mb-6 flex items-center gap-2.5">
          <Logo />
        </div>
        <div className="rounded-2xl border border-slate-800 bg-[#0d1420] p-6">
          <div className="flex items-center gap-2 text-amber-400"><CalendarCheck className="h-5 w-5" /><span className="eyebrow">Your appointment</span></div>
          <h1 className="mt-2 font-display text-2xl font-bold text-white">{info ? info.company : "Loading…"}</h1>

          {!info ? <div className="mt-6 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div> : (
            <>
              <div className="mt-4 space-y-2 rounded-xl border border-slate-800 bg-[#0B0F17] p-4 text-sm">
                <div className="font-semibold text-white" data-testid="appt-type">{info.type_label} · {info.name}</div>
                <div className="flex items-center gap-1.5 text-slate-400" data-testid="appt-when"><Clock className="h-3.5 w-3.5" />{info.when} · {info.duration_min}m</div>
                {info.address && <div className="flex items-center gap-1.5 text-slate-400"><MapPin className="h-3.5 w-3.5" />{info.address}</div>}
              </div>

              <a href={`${API}/public/appt/${token}/ics`} data-testid="appt-add-calendar" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-cyan-600/40 px-6 py-2.5 text-sm text-cyan-300 hover:border-cyan-400">
                <CalendarPlus className="h-4 w-4" /> Add to my calendar
              </a>

              {confirmed ? (
                <div className="mt-5 rounded-lg border border-emerald-600/40 bg-emerald-500/10 px-4 py-4 text-center text-emerald-300" data-testid="appt-confirmed">
                  <Check className="mx-auto mb-1 h-5 w-5" /> You're confirmed. See you then!
                  {info.booking_link && <div className="mt-3"><a href={info.booking_link} data-testid="appt-reschedule-link" className="text-xs text-cyan-300 underline">Need a different time? Reschedule</a></div>}
                </div>
              ) : (
                <div className="mt-5 space-y-3">
                  <button data-testid="appt-confirm-btn" onClick={confirm} disabled={busy} className="btn-amber inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-2.5 text-sm disabled:opacity-50">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Confirm this appointment
                  </button>
                  {info.booking_link && (
                    <a href={info.booking_link} data-testid="appt-reschedule-btn" className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-700 px-6 py-2.5 text-sm text-slate-200 hover:border-cyan-400">
                      <CalendarClock className="h-4 w-4" /> Request a different time
                    </a>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
